import React, { useLayoutEffect, useRef, useState } from "react";
import { CalendarDays, CalendarRange, Calendar, Minimize2, Target, Radio, TrendingUp, Paintbrush, Search, Hammer, PanelRight, EyeOff, Clock, Columns3, ArrowUpDown, BookOpen, ScrollText, Flag, Globe, Star, Bell, ToggleLeft } from "lucide-react";

// Fallback icons for page/access buttons that render without one.
const KEY_ICON = {
    "range-day": CalendarDays, "range-week": CalendarRange, "range-month": Calendar, compact: Minimize2, predict: Target, live: Radio,
    predicted: TrendingUp, format: Paintbrush, find: Search, builder: Hammer, extensions: PanelRight, spoiler: EyeOff, spoiler_since: Clock,
    delay: Clock, columns: Columns3, sortbar: ArrowUpDown, glossary: BookOpen, changelog: ScrollText, reports: Flag, leagues: Globe,
    favtoggles: Star, favourite: Star, tabfav: Star, notify: Bell,
};
const rgb = (s) => { const m = String(s).match(/[\d.]+/g); return m ? m.map(Number) : [148, 163, 184, 1]; };

// Wraps any existing toggle button so it reads exactly like a mobile Tab button
// (icon + name, flat column, top accent bar + tint when ON) — keeps its colour + action.
export function MobileTabCell({ id, node, full, iconOnly, tile }) {
    const ref = useRef(null);
    const [st, setSt] = useState({ on: false, c: "#94A3B8", hasIcon: true });
    useLayoutEffect(() => {
        const el = ref.current; if (!el) return undefined;
        const read = () => {
            const b = el.querySelector("button"); if (!b) return;
            const cs = getComputedStyle(b);
            const bgc = b.style.backgroundColor || cs.backgroundColor; const tc = b.style.color || cs.color;
            const [r, g, bl, a = 1] = rgb(bgc);
            const on = a >= 0.6 && r + g + bl > 120;
            const c = on ? `rgb(${r},${g},${bl})` : `rgb(${rgb(tc).slice(0, 3).join(",")})`;
            const hasIcon = !!b.querySelector("svg, img");
            setSt((p) => (p.on === on && p.c === c && p.hasIcon === hasIcon ? p : { on, c, hasIcon }));
        };
        read();
        let t = null;
        const mo = new MutationObserver(() => { read(); clearTimeout(t); t = setTimeout(read, 260); });
        mo.observe(el, { attributes: true, subtree: true, childList: true, attributeFilter: ["style", "class"] });
        return () => { mo.disconnect(); clearTimeout(t); };
    }, []);
    const Icon = KEY_ICON[id] || ToggleLeft;
    const forward = (e) => { if (e.target.closest("button")) return; ref.current?.querySelector("button")?.click(); };
    const shell = tile
        ? "relative flex flex-col items-center justify-center gap-1 h-14 min-w-0 rounded-lg border px-1 overflow-hidden [&_button]:!font-black [&_button]:!uppercase [&_button_*]:!font-black [&_button_*]:!uppercase [&_button_*]:!text-center [&_button_*]:!whitespace-normal [&_button_svg]:!h-4 [&_button_svg]:!w-4"
        : full
        ? "relative flex flex-col items-center justify-center gap-1 h-12 flex-1 min-w-fit px-2.5"
        : "relative inline-flex flex-col items-center justify-center h-9 w-9 rounded-full border";
    const btn = "[&:not([data-probe])_button]:!bg-transparent [&_button]:!border-0 [&_button]:!rounded-none [&_button]:!shadow-none [&_button]:!h-auto [&_button]:!w-auto [&_button]:!p-0 [&_button]:!min-w-0 [&_button]:!flex-col [&_button]:!items-center [&_button]:!justify-center [&_button]:!gap-1 [&_button]:!text-[9px] [&_button]:!font-semibold [&_button]:!normal-case [&_button]:!tracking-tight [&_button_svg]:!h-[18px] [&_button_svg]:!w-[18px] [&_button_*]:!text-[9px] [&_button_*]:!font-semibold [&_button_*]:!normal-case [&_button_*]:!tracking-tight [&:not([data-probe])_button]:![color:var(--tc)] [&:not([data-probe])_button_*]:![color:var(--tc)]";
    const hideLbl = !tile && (!full || iconOnly) ? "[&_button_span]:!hidden [&_button_.leading-none]:!hidden" : "";
    const tint = `${st.c.replace("rgb(", "rgba(").replace(")", ",0.15)")}`;
    const look = tile
        ? (st.on ? { background: tint, borderColor: st.c } : { background: "#15161F", borderColor: "rgba(255,255,255,0.1)" })
        : (st.on ? { background: tint, ...(full ? {} : { borderColor: st.c }) } : (full ? {} : { borderColor: "rgba(255,255,255,0.12)" }));
    return (
        <div ref={ref} onClick={forward} data-testid={`mobile-tabcell-${id}`}
            className={`${shell} ${btn} ${hideLbl} select-none transition-colors`}
            style={{ "--tc": st.c, ...look }}>
            {!st.hasIcon && <Icon className={tile ? "h-4 w-4 shrink-0" : "h-[18px] w-[18px] shrink-0"} strokeWidth={2} style={{ color: st.c }} />}
            {node}
            {full && !tile && st.on && <span className="absolute top-0 left-2.5 right-2.5 h-[2px] rounded-b-full" style={{ background: st.c }} aria-hidden="true" />}
        </div>
    );
}
